import { useLocation } from "@/compat/router-compat";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

const NOT_FOUND_TITLE = "Page not found — SyncAni";
const NOT_FOUND_DESCRIPTION =
  "This SyncAni page does not exist. Head back to the campfire room to join a spatial audio meeting.";

/** Rewrites the document head so a shared 404 link is not mistaken for the homepage. */
function applyNotFoundMetadata(): () => void {
  const previousTitle = document.title;
  document.title = NOT_FOUND_TITLE;

  const overrides: Array<[selector: string, value: string]> = [
    ['meta[name="description"]', NOT_FOUND_DESCRIPTION],
    ['meta[property="og:title"]', NOT_FOUND_TITLE],
    ['meta[property="og:description"]', NOT_FOUND_DESCRIPTION],
    ['meta[name="twitter:title"]', NOT_FOUND_TITLE],
    ['meta[name="twitter:description"]', NOT_FOUND_DESCRIPTION],
  ];

  const restores: Array<() => void> = [];
  for (const [selector, value] of overrides) {
    const element = document.querySelector<HTMLMetaElement>(selector);
    if (!element) continue;
    const previous = element.content;
    element.content = value;
    restores.push(() => {
      element.content = previous;
    });
  }

  const robots = document.createElement("meta");
  robots.name = "robots";
  robots.content = "noindex";
  document.head.appendChild(robots);

  return () => {
    document.title = previousTitle;
    for (const restore of restores) restore();
    robots.remove();
  };
}

const NotFound = () => {
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
    );
  }, [location.pathname]);

  useEffect(() => applyNotFoundMetadata(), []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center">
        <h1 className="mb-4 text-xl font-bold">404</h1>
        <p className="mb-4 text-xl text-muted-foreground">
          {t("error.notFound")}
        </p>
        <Button asChild variant="link">
          <a href="/">{t("error.returnHome")}</a>
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
